import { Spin } from 'antd';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { MessageStates } from 'containers/MessageBox';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { formatDisplayInteger } from 'i18n/arabicDigits';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AccountType } from 'store/api/enums';
import { turnMessageOn } from 'store/app/actions';
import { MessageData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import type { Organisation } from 'store/organisation/models';
import { getReceiptStatisticsAll } from 'store/organisation/selectors';
import { isReceiptsEntityProvider } from 'utils/entity-capture-column';
import { unlockLeaveEntityNavigation } from 'utils/leave-entity-navigation-lock';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import {
  buildPwaEntityDetailsNavigateUrl,
  getPwaCaptureEntities,
  preparePwaCaptureNavigation,
  shouldShowPwaDisconnected,
} from 'utils/pwa-nav';
import {
  clearPwaLastEntity,
  consumeSkipPwaHomeRestore,
  getPwaLastEntity,
  isPwaHomePickerPath,
  resolvePwaLastWindowNavigateUrl,
  setPwaLastEntity,
  subscribePwaLastEntity,
} from 'utils/pwa-last-entity';
import { getPwaEntityReviewCounts } from 'utils/pwa-entity-pending-review';
import { PINNED_ENTITY_IDS_PREF } from 'utils/use-pwa-capture-entities';
import { PwaIcon } from 'pwa/components/Icon';
import {
  PwaChip,
  PwaSearch,
  PwaSection,
  PwaTopBar,
} from 'pwa/components/ui';
import 'pwa/styles/tokens.css';
import 'pwa/styles/pwa-home.css';

export type PwaHomeEntityListProps = {
  entities: Organisation[] | undefined;
  pinnedEntityIds?: string[];
  loading?: boolean;
};

type StatFilter = 'all' | 'review' | 'post' | 'disconnected';

type EntityRowMetrics = {
  pendingReview: number;
  readyToPost: number;
  needsReconnect: boolean;
  hideReadyToPost: boolean;
};

const getGreetingPeriod = (): 'morning' | 'afternoon' | 'evening' => {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

const getEntityMetrics = (
  entity: Organisation,
  receiptStatistics: ReturnType<typeof getReceiptStatisticsAll>
): EntityRowMetrics => {
  const { pendingReview, readyToPost } = getPwaEntityReviewCounts(
    entity,
    receiptStatistics
  );
  const needsReconnect = shouldShowPwaDisconnected(entity);
  const hideReadyToPost = isReceiptsEntityProvider(entity.providerName);

  return {
    pendingReview,
    readyToPost,
    needsReconnect,
    hideReadyToPost,
  };
};

export const PwaHomeEntityList: React.FC<PwaHomeEntityListProps> = ({
  entities,
  pinnedEntityIds = [],
  loading = false,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { localizeCountry } = useCountryLabel();
  const currentUser = useSelector(getAuthUser);
  const receiptStatistics = useSelector(getReceiptStatisticsAll);
  const preferences = usePreferences();
  const autoOpenAttemptedRef = React.useRef(false);

  const [search, setSearch] = React.useState('');
  const [statFilter, setStatFilter] = React.useState<StatFilter>('all');
  const [lastEntityTick, setLastEntityTick] = React.useState(0);

  React.useEffect(
    () =>
      subscribePwaLastEntity(() => {
        setLastEntityTick((tick) => tick + 1);
      }),
    []
  );

  const isClientUser =
    !!currentUser &&
    AccountType[currentUser.accountType] === AccountType.CLIENT;

  const list = React.useMemo(
    () => getPwaCaptureEntities(entities, pinnedEntityIds),
    [entities, pinnedEntityIds]
  );

  const metricsById = React.useMemo(() => {
    const map = new Map<string, EntityRowMetrics>();
    list.forEach((entity) => {
      map.set(entity.uuid, getEntityMetrics(entity, receiptStatistics));
    });
    return map;
  }, [list, receiptStatistics]);

  const toReview = React.useMemo(
    () =>
      Array.from(metricsById.values()).reduce(
        (sum, m) => sum + (m.needsReconnect ? 0 : m.pendingReview),
        0
      ),
    [metricsById]
  );
  const toPost = React.useMemo(
    () =>
      Array.from(metricsById.values()).reduce(
        (sum, m) =>
          sum + (m.needsReconnect || m.hideReadyToPost ? 0 : m.readyToPost),
        0
      ),
    [metricsById]
  );
  const disconnectedCount = React.useMemo(
    () =>
      Array.from(metricsById.values()).filter((m) => m.needsReconnect).length,
    [metricsById]
  );

  const selectedEntityId = React.useMemo(() => {
    const id = getPwaLastEntity()?.organisationId;
    if (!id || !list.some((entity) => entity.uuid === id)) return null;
    return id;
  }, [list, lastEntityTick]);

  const filteredList = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = list.filter((entity) => {
      const metrics = metricsById.get(entity.uuid);
      if (!metrics) return false;
      if (statFilter === 'review' && !(metrics.pendingReview > 0 && !metrics.needsReconnect)) {
        return false;
      }
      if (
        statFilter === 'post' &&
        !(metrics.readyToPost > 0 && !metrics.needsReconnect && !metrics.hideReadyToPost)
      ) {
        return false;
      }
      if (statFilter === 'disconnected' && !metrics.needsReconnect) {
        return false;
      }
      if (!q) return true;
      const label = (entity.friendlyName || entity.legalName || '').toLowerCase();
      const vat = (entity.taxNumber || '').toLowerCase();
      const country = (entity.country || '').toLowerCase();
      return label.includes(q) || vat.includes(q) || country.includes(q);
    });
    if (!selectedEntityId) return filtered;
    const index = filtered.findIndex((entity) => entity.uuid === selectedEntityId);
    if (index <= 0) return filtered;
    const ordered = filtered.slice();
    const [selected] = ordered.splice(index, 1);
    ordered.unshift(selected);
    return ordered;
  }, [list, metricsById, search, statFilter, selectedEntityId]);

  // PWA / phone open on Home: restore last *entity* only if that was the
  // last screen. Closing on Home is stored as /main and stays on Home.
  // Explicit Home from the bottom nav must never bounce back.
  React.useEffect(() => {
    unlockLeaveEntityNavigation();
    if (autoOpenAttemptedRef.current || loading) {
      return;
    }

    const explicitHome =
      consumeSkipPwaHomeRestore() ||
      (location.state as { pwaExplicitHome?: boolean } | null)
        ?.pwaExplicitHome === true;

    if (explicitHome) {
      autoOpenAttemptedRef.current = true;
      return;
    }

    const last = getPwaLastEntity();
    if (!last) {
      if (entities) {
        autoOpenAttemptedRef.current = true;
      }
      return;
    }

    if (last.pathname && isPwaHomePickerPath(last.pathname)) {
      autoOpenAttemptedRef.current = true;
      return;
    }

    const needsEntityList = Boolean(
      last.organisationId ||
        last.pathname?.includes('/entities/organisation/')
    );
    if (needsEntityList && !entities) {
      return;
    }

    autoOpenAttemptedRef.current = true;
    const target = resolvePwaLastWindowNavigateUrl(last, entities);
    if (!target) {
      if (needsEntityList) {
        clearPwaLastEntity();
      }
      return;
    }

    preparePwaCaptureNavigation();
    navigate(target, { replace: true });
  }, [entities, loading, navigate, location.state]);

  const rememberEntity = (entity: Organisation) => {
    setPwaLastEntity(entity.uuid);
  };

  const onSelect = (entity: Organisation) => {
    rememberEntity(entity);
    preparePwaCaptureNavigation();
    navigate(buildPwaEntityDetailsNavigateUrl(entity));
  };

  const setPinned = (entity: Organisation, pinned: boolean) => {
    if (!currentUser?.userUuid) return;
    const currentlyPinned = pinnedEntityIds.includes(entity.uuid);
    if (pinned === currentlyPinned) return;
    const next = pinned
      ? [...pinnedEntityIds, entity.uuid]
      : pinnedEntityIds.filter((id) => id !== entity.uuid);
    preferences(
      PreferenceActions.SET,
      PreferenceSections.ENTITIES,
      { name: PINNED_ENTITY_IDS_PREF, value: next },
      currentUser.userUuid
    );
    const message: MessageData = {
      title: t('organisation.pin-entity-success-title'),
      description: pinned
        ? t('organisation.pin-entity-success')
        : t('organisation.unpin-entity-success'),
      type: MessageStates.SUCCESS,
    };
    dispatch(turnMessageOn(message));
  };

  const toggleStatFilter = (next: StatFilter) => {
    setStatFilter((current) => (current === next ? 'all' : next));
  };

  const displayName =
    currentUser?.firstName?.trim() ||
    currentUser?.email?.split('@')[0] ||
    t('organisation.pwa-home-fallback-name');

  const greeting = t(`organisation.pwa-home-greeting-${getGreetingPeriod()}`, {
    name: displayName,
  });

  const subtitle =
    toReview > 0
      ? t('organisation.pwa-home-subtitle-review', {
          count: formatDisplayInteger(toReview, i18n.language),
          entities: formatDisplayInteger(list.length, i18n.language),
        })
      : t('organisation.pwa-home-subtitle-idle', {
          entities: formatDisplayInteger(list.length, i18n.language),
        });

  return (
    <div
      className="scope-pwa-home-entities pwa-host"
      data-shell="pwa"
    >
      <div className="pwa">
        <PwaTopBar logo />
        <main className="pwa-main">
          <div className="pwa-largetitle">
            <h2>{greeting}</h2>
            <p>{subtitle}</p>
          </div>

          <PwaSection>
            <div className="pwa-stats">
              <button
                type="button"
                className="pwa-stat warn"
                aria-pressed={statFilter === 'review'}
                onClick={() => toggleStatFilter('review')}
              >
                <b>{formatDisplayInteger(toReview, i18n.language)}</b>
                <span>{t('organisation.pwa-home-stat-to-review')}</span>
              </button>
              <button
                type="button"
                className="pwa-stat ok"
                aria-pressed={statFilter === 'post'}
                onClick={() => toggleStatFilter('post')}
              >
                <b>{formatDisplayInteger(toPost, i18n.language)}</b>
                <span>{t('organisation.pwa-home-stat-to-post')}</span>
              </button>
              <button
                type="button"
                className="pwa-stat danger"
                aria-pressed={statFilter === 'disconnected'}
                onClick={() => toggleStatFilter('disconnected')}
              >
                <b>{formatDisplayInteger(disconnectedCount, i18n.language)}</b>
                <span>{t('organisation.pwa-home-stat-disconnected')}</span>
              </button>
            </div>
          </PwaSection>

          <PwaSection title={t('organisation.entities')}>
            <PwaSearch
              id="pwa-entity-search"
              placeholder={t('organisation.pwa-home-search-entities')}
              value={search}
              onChange={setSearch}
            />

            {loading && !list.length ? (
              <div className="pwa-home-loading">
                <Spin />
              </div>
            ) : filteredList.length === 0 ? (
              <p className="pwa-home-empty">
                {list.length === 0
                  ? t('organisation.pwa-home-select-entity-empty')
                  : t('organisation.pwa-home-search-empty')}
              </p>
            ) : (
              <div className="pwa-list">
                {filteredList.map((entity) => {
                  const label = entity.friendlyName || entity.legalName;
                  const isPinned = pinnedEntityIds.includes(entity.uuid);
                  const metrics = metricsById.get(entity.uuid)!;
                  const pinAriaLabel = isPinned
                    ? t('organisation.unpin-entity')
                    : t('organisation.pin-entity');
                  const countryLabel = localizeCountry(
                    entity.country,
                    entity.country
                  );
                  const vatLabel = entity.taxNumber
                    ? t('organisation.pwa-home-entity-vat', {
                        vat: entity.taxNumber,
                      })
                    : t('organisation.pwa-home-entity-no-vat');

                  return (
                    <div
                      key={entity.uuid}
                      className={
                        entity.uuid === selectedEntityId
                          ? 'pwa-row-block pwa-entity is-current'
                          : 'pwa-row-block pwa-entity'
                      }
                      role="button"
                      tabIndex={0}
                      aria-current={
                        entity.uuid === selectedEntityId ? 'true' : undefined
                      }
                      onClick={() => onSelect(entity)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelect(entity);
                        }
                      }}
                    >
                      <div className="pwa-entity-top">
                        <span className="pwa-avatar" aria-hidden>
                          <ProviderIcon
                            providerName={entity.providerName}
                            alt=""
                            width={28}
                            height={28}
                          />
                        </span>
                        <div className="pwa-row-body">
                          <span className="pwa-row-title">{label}</span>
                          <span className="pwa-row-sub">
                            {vatLabel}
                            {countryLabel ? ` · ${countryLabel}` : null}
                          </span>
                        </div>
                        {!isClientUser ? (
                          <button
                            type="button"
                            className="pwa-iconbtn pwa-pin"
                            aria-pressed={isPinned}
                            aria-label={pinAriaLabel}
                            onClick={(event) => {
                              event.stopPropagation();
                              setPinned(entity, !isPinned);
                            }}
                          >
                            <PwaIcon
                              name="pin"
                              size={19}
                              fill={isPinned ? 'currentColor' : undefined}
                            />
                          </button>
                        ) : null}
                      </div>
                      <div className="pwa-entity-counts">
                        {metrics.needsReconnect ? (
                          <PwaChip tone="danger" icon="link">
                            {t('organisation.pwa-home-chip-disconnected')}
                          </PwaChip>
                        ) : (
                          <>
                            <PwaChip
                              tone={metrics.pendingReview ? 'warn' : undefined}
                              icon="review"
                            >
                              {t('navigation.review-pending-count', {
                                count: formatDisplayInteger(
                                  metrics.pendingReview,
                                  i18n.language
                                ),
                              })}
                            </PwaChip>
                            {!metrics.hideReadyToPost ? (
                              <PwaChip
                                tone={metrics.readyToPost ? 'ok' : undefined}
                                icon="check"
                              >
                                {t('organisation.pwa-home-chip-to-post', {
                                  count: formatDisplayInteger(
                                    metrics.readyToPost,
                                    i18n.language
                                  ),
                                })}
                              </PwaChip>
                            ) : null}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </PwaSection>
        </main>
      </div>
    </div>
  );
};

export default PwaHomeEntityList;
